import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import Flickity from 'flickity';
import * as uuid from 'uuid';
import { FlickityUtils } from './utils/flickity-utils';
import { DomUtils } from '@streamamg/amg-common';

@Component({
  selector: 'amg-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent implements OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('carouselContainer') carouselContainerRef: ElementRef;
  @HostBinding('attr.id') carouselId: string;
  @HostBinding('class.amg-carousel') hideComponent: boolean = true;

  /**
   * Flickity options: https://flickity.metafizzy.co/options.html
   */
  @Input() flickityOptions?: Flickity.Options;

  /**
   * Template Top.
   *
   * Define an Angular template. Example:
   *
   * <ng-template #top>This is the top template</ng-template>
   */
  @Input() topCarouselTemplate?: TemplateRef<HTMLElement>;

  /**
   * Template Bottom.
   *
   * Define an Angular template. Example:
   *
   * <ng-template #bottom>This is the bottom template</ng-template>
   */
  @Input() bottomCarouselTemplate?: TemplateRef<HTMLElement>;

  /*/!**
   * Template Item.
   * Define an Angular template. Example:
   * <ng-template #itemTemplate>This is the item template</ng-template>
   *!/
  @Input() itemTemplate: TemplateRef<HTMLElement>;

  /!**
   * List of items (better to pass the 'items' in the component content)
   *!/
  @Input() items: unknown[];*/

  /**
   * Flickity Reference
   */
  @Output() triggerFlickityObj: EventEmitter<Flickity>;

  /**
   * Flickity Reference
   */
  public flickityReference: Flickity;

  /**
   * Flickity is loaded
   */
  public isFlickityLoaded: boolean;

  private triggerFlickityInitAfterRender: boolean;

  private oldElementWidth: number;

  constructor(
    private cd: ChangeDetectorRef) {
    this.carouselId = `c-${uuid.v4()}`;
    this.triggerFlickityInitAfterRender = false;

    this.isFlickityLoaded = false;
    this.triggerFlickityObj = new EventEmitter<Flickity>();
    this.triggerFlickityInitAfterRender = true;
  }

  ngOnChanges(): void {

    this.refreshCarousel();
  }

  ngAfterViewInit(): void {

    if (this.triggerFlickityInitAfterRender) {

      this.initializeCarousel();

      this.triggerFlickityInitAfterRender = false;
    }
  }

  @HostListener('window:forceRefreshCarousel', ['$event'])
  @HostListener('window:resize', ['$event'])
  @HostListener('window:orientationchange', ['$event'])
  private recreateCarousel(event: Event): void {

    if (this.flickityReference && (event.type === 'forceRefreshCarousel' || this.oldElementWidth !== document.documentElement.clientWidth)) {
      this.oldElementWidth = document.documentElement.clientWidth;
      this.refreshCarousel();
    }
  }

  private initializeCarousel(): void {

    this.flickityReference = new Flickity(this.carouselContainerRef.nativeElement, {
      groupCells: true,
      contain: true,
      cellAlign: 'center',
      prevNextButtons: true,
      pageDots: false,
      // friction: 0.10, // default: 0.28. Higher friction = stickier and less bouncy
      // selectedAttraction: 0.005, // default: 0.025. Higher attraction makes the slider move faster
      percentPosition: false,
      on: {
        ready: () => {
          this.setCarouselElementInViewport();
          this.setCarouselElementAriaHidden();
        },
        change: () => {
          this.carouselContainerRef.nativeElement.querySelectorAll('.flickity-slider > * > *').forEach((element: HTMLElement) => {
            element.classList.remove('not-in-view', 'in-view');
          });
        },
        settle: (index) => {
          this.setCarouselElementInViewport();
          this.setCarouselElementAriaHidden();
        },
      },
      ...(this.flickityOptions ? this.flickityOptions: {}),
    });

    FlickityUtils.stopSlideOnItemClick(
      this.flickityReference,
      this.carouselContainerRef.nativeElement,
      this.carouselId,
      'div.amg-carousel__item',
    );

    this.isFlickityLoaded = true;
    this.cd.detectChanges();

    this.triggerFlickityObj.emit(this.flickityReference);
  }

  public destroyCarousel(): void {

    if (this.flickityReference) {
      this.flickityReference.destroy();
      // @ts-ignore
      this.flickityReference = null;
    }
  }

  private setCarouselElementInViewport(): void {

    this.carouselContainerRef.nativeElement.querySelectorAll('.flickity-slider > * > *').forEach((element: HTMLElement) => {
      if (DomUtils.isInViewport(element, 'h')) {
        element.classList.remove('not-in-view');
        element.classList.add('in-view');
      } else {
        element.classList.remove('in-view');
        element.classList.add('not-in-view');
      }
    });
  };

  //Flickity issue about accessibility
  private setCarouselElementAriaHidden(): void {
    this.carouselContainerRef.nativeElement.querySelectorAll('.flickity-slider > *').forEach((element: HTMLElement) => {
      element.setAttribute('aria-hidden', 'false');
    });
  }

  public refreshCarousel(): void {
    this.isFlickityLoaded = false;
    this.cd.detectChanges();

    setTimeout(() => {

      this.destroyCarousel();
      this.initializeCarousel();

    }, 1);
  }

  ngOnDestroy(): void {
    this.destroyCarousel();
  }
}
